/**
 *  @class
 *  @function TopbarSticky
 *  Pins the topbar section ("Topbar Sticky" setting) and publishes --topbar-offset,
 *  the height the sticky header and other sticky elements must leave free for it.
 *  "On scroll up" follows the header's pinned state when the header is also "On scroll up",
 *  so both bars hide and return together; otherwise it tracks the scroll direction itself.
 */

if (!customElements.get('topbar-sticky')) {
  class TopbarSticky extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section');
      if (!this.section) return;

      this.onScrollUp = this.dataset.mode === 'on-scroll-up';
      this.hideThreshold = 200; // same distance the header scrolls down before it hides
      this.lastScrollY = window.scrollY;
      this.scrollDistance = 0;
      this.isHidden = false;

      this.section.classList.add('topbar-sticky');

      this.resizeObserver = new ResizeObserver(() => this.setOffset());
      this.resizeObserver.observe(this.section);

      if (this.onScrollUp) {
        this.onScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });

        if (this.followsHeader) {
          this.bodyObserver = new MutationObserver(() => this.onScroll());
          this.bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        }
      }

      this.setOffset();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.bodyObserver) this.bodyObserver.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.section) this.section.classList.remove('topbar-sticky', 'topbar-sticky--hidden');

      // in the theme editor the re-rendered section may already have connected its own copy
      if (!document.querySelector('topbar-sticky')) {
        document.documentElement.style.removeProperty('--topbar-offset');
      }
    }

    get followsHeader() {
      return !!document.querySelector('.header-section header[data-sticky-type="on-scroll-up"]');
    }

    onScroll() {
      const scrollY = window.scrollY;
      const direction = scrollY > this.lastScrollY ? 'down' : 'up';

      if (direction !== this.lastDirection) {
        this.scrollDistance = 0;
        this.lastDirection = direction;
      } else {
        this.scrollDistance += Math.abs(scrollY - this.lastScrollY);
      }
      this.lastScrollY = scrollY;

      let hide;
      if (scrollY <= this.section.offsetHeight) {
        hide = false;
      } else if (this.followsHeader) {
        hide = !document.body.classList.contains('header-pinned');
      } else if (direction === 'up') {
        hide = false;
      } else {
        hide = this.isHidden || this.scrollDistance >= this.hideThreshold;
      }

      if (hide !== this.isHidden) {
        this.isHidden = hide;
        this.section.classList.toggle('topbar-sticky--hidden', hide);
        this.setOffset();
      }
    }

    setOffset() {
      // exact (fractional) height: rounding can leave a hairline gap above the header on phones
      const offset = this.isHidden ? 0 : this.section.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--topbar-offset', `${offset}px`);
    }
  }

  customElements.define('topbar-sticky', TopbarSticky);
}
